import {
  devices,
  Devices,
  HOME_URL,
  SubDomainBlockList,
} from '@common/constants/shared';
import { useParams } from '@common/routes/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  Platform,
  NativeModules,
  Image,
} from 'react-native';
import {
  Platform as AppPlatform,
  AppVersion,
  Platform as PlatformType,
} from 'click-types';
import IFrameShare from '@common/components/iframeshare';
import styles from './style';
import { WebView } from 'react-native-webview';
import useAppDetail from '@common/hooks/app';
import { useSelector } from 'react-redux';
import { dimensionSelector } from '@common/redux/selectors/page';
import { checkMobileLayout } from '@common/utils/screen';
import useSocketConnection from '@common/hooks/useSocketAuthentication';
import Loading from '@common/components/Loading';
import ShareCustomDomain from '../ShareScreen/ShareScreenCustomDomain';
import clickLogo from '@common/assets/images/click-logo.png';
import { Button as RNButton } from 'react-native-paper';
import ArrowForwardIcon from '@common/components/Icons/ArrowForwardIcon';
import { getMessage } from '@common/constants/locale';
import { setupManifest } from '@common/utils/setupManifest';
import Render from '../RenderScreen';
import { SelectDevice } from '@common/screens/ShareScreenUrl/ResponsiveType';
import { deviceSelectedSelector } from '@common/redux/selectors/devices';
import { useAppSelector } from '@common/redux/hooks';
import { useCollections } from '@common/hooks/collections';

const ShareScreenView = ({ appId }: any) => {
  const { isLoading, data } = useAppDetail({
    appId: appId,
    isShareScreen: true,
  });
  useCollections({ appId });
  const dimension = useSelector(dimensionSelector);
  const isMobile = checkMobileLayout(dimension);
  const deviceSelected = useAppSelector(deviceSelectedSelector);
  const deviceInfo = devices[deviceSelected];
  const [locale, setLocale] = useState('ja');
  let showBanner = true;
  if (data?.metadata && 'showBanner' in data?.metadata) {
    showBanner = data?.metadata.showBanner as any;
  }

  // Check if this is a V4 responsive app
  const { version, platform } = data ?? {
    version: AppVersion.V3,
    platform: PlatformType.MOBILE,
  };
  const isV4Responsive =
    version === AppVersion.V4 && platform === PlatformType.RESPONSIVE;

  if (data) {
    let link: any = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = data.icon;
  }

  const handleClickCreate = useCallback(() => {
    window.open(HOME_URL, '_blank');
  }, []);

  const urlSearchParams = new URLSearchParams(window.location.search);
  const isTutorial = urlSearchParams.get('tutorial');
  const newUri = isV4Responsive
    ? `/preview-v4/${appId}?${urlSearchParams.toString()}`
    : `/preview/${appId}?${urlSearchParams.toString()}`;

  useEffect(() => {
    setupManifest(appId, data);
  }, [appId, data]);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    if (urlSearchParams.has('locale')) {
      const locale = urlSearchParams.get('locale') ?? 'ja';
      setLocale(locale);
    }
  }, []);

  useEffect(() => {
    setupManifest(appId, data);
  }, [appId, data]);

  if (isMobile) {
    return <Render />;
  }

  return !data ? null : data.platform === AppPlatform.WEB ? (
    <View>
      <WebView
        source={{ uri: newUri }}
        style={{
          width: '100%',
          height: '100dvh',
        }}
        scalesPageToFit={true}
      />
    </View>
  ) : (
    <SafeAreaView style={styles.containerCol}>
      {!isTutorial ? (
        <>
          {showBanner && (
            <View style={styles.navBarWrapper}>
              <View style={[styles.navBar]}>
                <View style={styles.childBar}>
                  <Text>
                    {getMessage({
                      id: 'share.modal.link.productIntro',
                      locale,
                    })}
                  </Text>
                </View>
                <View style={styles.childBar}>
                  <Image
                    source={{ uri: clickLogo }}
                    style={{
                      width: 80,
                      height: 45,
                    }}
                  />
                </View>
                <View style={styles.childBar}>
                  <Text>
                    {getMessage({ id: 'share.modal.link.developedBy', locale })}
                  </Text>
                </View>
                <View>
                  <RNButton
                    mode="outlined"
                    onPress={() => handleClickCreate()}
                    contentStyle={styles.content}
                    labelStyle={styles.label}
                    style={styles.button}
                  >
                    {getMessage({ id: 'share.modal.link.startNow', locale })}
                    <View style={styles.iconContainer}>
                      <ArrowForwardIcon />
                    </View>
                  </RNButton>
                </View>
              </View>
            </View>
          )}
          <SelectDevice />
          <View
            nativeID="share-screen-view-iframe-share-container"
            style={[
              {
                flex: 1,
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#F2F5F7',
                overflow: 'scroll',
              },
            ]}
          >
            <IFrameShare appId={appId} hasBorder={false} />
          </View>
        </>
      ) : (
        <View></View>
      )}
    </SafeAreaView>
  );
};

const ShareScreenUrl = () => {
  const currentHostName = window.location.hostname;
  const regexHost = /(\.click\.dev)$/;
  let subDomain =
    regexHost.test(currentHostName) && currentHostName.replace(regexHost, '');

  const isMobile = () => {
    try {
      document.createEvent('TouchEvent');
      return true;
    } catch (e) {
      return false;
    }
  };
  const params = useParams();

  // authentication SocketIO (forced to run first !)
  if (params.appId) {
    const { loading: reAuthLoading } = useSocketConnection();
    if (reAuthLoading) {
      return <Loading />;
    }
  }

  if (Platform.OS == 'web') {
    const urlSearchParams = new URLSearchParams(window.location.search);

    const urlScheme = urlSearchParams.get('app');

    if (urlScheme) {
      // redirect to app for share SNS function
      const iosAppId = urlSearchParams.get('iosAppId');
      const iosAppName = urlSearchParams.get('iosAppName');
      const searchParams = window.location.search;

      const locale =
        NativeModules?.SettingsManager?.settings?.AppleLocale ||
        NativeModules?.SettingsManager?.settings?.AppleLanguages?.[0];

      const localeArr = typeof locale === 'string' ? locale.split('_') : [];

      const country = localeArr.length
        ? localeArr[localeArr.length - 1].toLowerCase()
        : null;

      var tryClose = function () {
        location.replace('about:blank');
      };
      var start = Date.now();
      var fallbackToStore = function () {
        var now = Date.now();
        if (now - start > 3000) {
          tryClose();
        } else {
          if (navigator.userAgent.toLowerCase().indexOf('android') > -1) {
            window.location.replace(`market://details?id=${urlScheme}`);
          }
          if (navigator.userAgent.toLowerCase().indexOf('iphone') > -1) {
            if (iosAppId && iosAppName) {
              const urlAppStore = `https://apps.apple.com/${
                country ? country : `jp`
              }/app/${iosAppName}/${iosAppId}`;

              window.location.replace(urlAppStore);
            }
          }
          setTimeout(tryClose, 5000);
        }
      };

      var openApp = function () {
        window.location.replace(`${urlScheme}://${searchParams}`);
      };
      var triggerAppOpen = function () {
        openApp();
        setTimeout(fallbackToStore, 1000);
      };

      triggerAppOpen();
      return null;
    }

    const customDomain = urlSearchParams.get('domain');

    const isState = urlSearchParams.has('state');
    const state = urlSearchParams.get('state'); // state = location.href | state = location.pathname;
    if (isState) {
      const codeAuth = urlSearchParams.get('code');
      const isLineBrowser = navigator.userAgent.indexOf('Line/') !== -1;
      // line browser
      if (state && isLineBrowser) {
        let path: any = state;
        if (path.includes('/preview')) {
          path = path.replace('/preview', '') + `?code=${codeAuth}&type=line`;
        }
        if (path.includes('/share-app')) {
          path = path.replace('/share-app', '') + `?code=${codeAuth}&type=line`;
        }
        window.location.href = window.location.origin + path;
        return null;
      }

      // web browser
      if (state?.includes('/preview') || state?.includes('/share-app')) {
        const url = new URL(
          state.replace('/preview', '').replace('/share-app', '')
        );
        url.searchParams.set('code', codeAuth || '');
        url.searchParams.set('type', 'line');
        window.location.href = url.href;
        return (
          <View>
            <Text>Redirect...</Text>
          </View>
        );
      }

      // web browser on native-app
      if (isMobile()) {
        const launchApp = () => {
          // redirect to app
          window.location.replace(`${state}://?code=${codeAuth}`);
        };
        if (state && codeAuth) launchApp();
        return null;
      }
    }

    if (!params?.appId) {
      if (customDomain) {
        return <ShareCustomDomain domain={customDomain} />;
      }
      if (subDomain && !SubDomainBlockList.includes(subDomain)) {
        return <ShareCustomDomain domain={subDomain} />;
      }
      return (
        <View>
          <Text>Router not found</Text>
        </View>
      );
    }
    return <ShareScreenView appId={params.appId} />;
  } else {
    return (
      <View>
        <Text>Cannot support for OS version</Text>
      </View>
    );
  }
};

export default ShareScreenUrl;
